<template>
    <div class="widget widget--kyc">
        <div class="label label--half label--document-type label--bottom-padding">
            {{ $t('kycDocumentTypes.documentType') }}:

            <multiselect :value="documentType"
                :options="documentTypes"
                :allow-empty="false"
                :show-labels="false"
                track-by="label"
                label="label"
                :placeholder="$t('kycDocumentTypes.documentTypeHint')"
                :class="['label__multiselect', { 'label__multiselect--active': documentType }]"
                @select="(args) => $emit('update:documentType', args)"
            >
                <template slot="singleLabel"
                    slot-scope="props"
                >
                    {{ props.option.label ? props.option.label : documentType }}
                </template>
                <template slot="option"
                    slot-scope="props"
                >
                    {{ props.option.label }}
                </template>
                <span slot="noResult">{{ $t('registerMobileNumber.noResultsCountries') }}</span>
            </multiselect>

            <span class="widget__note">{{ $t('kycDocumentTypes.note') }}</span>
        </div>

        <div class="label label--half label--bottom-padding">
            <custom-input :value="documentNumber"
                name="documentNumber"
                type="text"
                :label="$t('kycDocumentTypes.documentNumber')"
                :placeholder="$t('kycDocumentTypes.documentNumber')"
                :validations="documentNumberValidations"
                :validate-on-mount="Boolean(documentNumber)"
                :is-valid.sync="validDocumentNumber"
                required
                @input="$emit('update:documentNumber', $event)"
            />
        </div>

        <div class="label--half expiry-date">
            <custom-input :value="expiryDate"
                name="expiryDate"
                type="date"
                :min="minExpiryDate"
                max="2099-12-31"
                :label="$t('kycDocumentTypes.expiryDate')"
                :placeholder="$t('kycDocumentTypes.expiryDateHint')"
                :validations="expiryDateValidations"
                :validate-on-mount="Boolean(expiryDate)"
                :disabled="doesNotExpire"
                :hide-hint="doesNotExpire"
                required
                :is-valid.sync="validExpiryDate"
                @input="$emit('update:expiryDate', $event)"
            />
        </div>

        <div class="label label--half label--bottom-padding issuing-country">
            {{ $t('kycDocumentTypes.issuingCountry') }}:

            <country-dropdown :country="issuingCountry"
                :placeholder="$t('kycDocumentTypes.issuingCountryHint')"
                @update:country="(args) => $emit('update:issuingCountry', args)"
            />
        </div>

        <div class="label label--half label--bottom-padding does-not-expire">
            <input-checkbox
                :value="doesNotExpire"
                :text="$t('kycDocumentTypes.doesNotExpire')"
                :maxWidth="false"
                noBackground
                @toggleCheckBox="onDocumentExpiryChange"
            />
        </div>

        <div v-if="isItalianUser"
            class="label label--half label--bottom-padding codice-fiscale"
        >
            <custom-input :value="codiceFiscale"
                name="codiceFiscale"
                :label="$t('kycDocumentTypes.codiceFiscale')"
                :placeholder="$t('kycDocumentTypes.codiceFiscale')"
                :validations="codiceFiscaleValidations"
                required
                :isValid.sync="isCodiceFiscaleValid"
                @input="$emit('update:codiceFiscale', $event)"
            />
        </div>
    </div>
</template>

<script>
// 3rd party
import {
    mapGetters,
} from 'vuex';

// Validations
import {
    dateGreaterThanToday,
} from '@shared/validations';

import {
    noEmojisAllowedForWeb,
} from '@webapp/helpers/validations';

import {
    codiceFiscaleFormat, validExpiryDate,
} from '@shared/validations/customer-first';

// Components
import inputCheckbox from '@webapp/components/inputs/input-checkbox.vue';
import countryDropdown from '@webapp/components/country-dropdown/country-dropdown.vue';
import customInput from '@webapp/components/form/custom-input.vue';

export default {
    name: 'step-two-document',
    components: {
        inputCheckbox,
        countryDropdown,
        customInput,
    },
    props: {
        documentType: {
            type: Object,
            default: null,
        },
        documentNumber: {
            type: String,
            default: '',
        },
        issuingCountry: {
            type: Object,
            default: null,
        },
        expiryDate: {
            type: String,
            default: '',
        },
        codiceFiscale: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            documentTypes: [],
            doesNotExpire: false,
            isCodiceFiscaleValid: false,
            validDocumentNumber: false,
            validExpiryDate: false,
        };
    },
    computed: {
        ...mapGetters([
            'kycDocumentTypes',
            'isItalianUser',
            'getTranslatedStr',
        ]),

        isFormValid() {
            return Boolean(this.documentType &&
                this.validDocumentNumber &&
                (this.validExpiryDate || this.doesNotExpire) &&
                (!this.isItalianUser || this.isCodiceFiscaleValid) &&
                this.issuingCountry);
        },

        documentNumberValidations() {
            return [noEmojisAllowedForWeb];
        },

        codiceFiscaleValidations() {
            return [codiceFiscaleFormat];
        },

        expiryDateValidations() {
            return [dateGreaterThanToday, validExpiryDate];
        },

        minExpiryDate() {
            const tomorrow = new Date();

            tomorrow.setDate(new Date().getDate() + 1);

            return tomorrow.toISOString().split('T')[0];
        },
    },
    watch: {
        isFormValid: {
            handler(value) {
                if (!value) {
                    this.$emit('inProgress');

                    return;
                }

                this.$emit('completed');

            },
            immediate: true,
        },
    },
    beforeMount() {
        this.setKycDocumentTypes();
    },
    methods: {

        /**
         * On focus date of birth input
         *
         * @returns {void}
         */
        onFocusExpiryDate() {
            if (this.$refs.expiryDate.type !== 'date') {
                this.$refs.expiryDate.type = 'date';
            }
        },

        /**
         * On blur date of birth input
         *
         * @returns {void}
         */
        onBlurExpiryDate() {
            if (!this.expiryDate) {
                this.$refs.expiryDate.type = 'text';
            }
        },

        /**
         * Set the list of document types
         *
         * @returns {void}
         */
        setKycDocumentTypes() {
            // Get the document types

            this.documentTypes = this.kycDocumentTypes.map((documentType) => ({
                label: this.getTranslatedStr(documentType, 'name', documentType.name),
                value: documentType.id,
                countryFormats: documentType.kycDocumentTypeCountry || [],
                format: documentType.format,
            }));
        },

        onDocumentExpiryChange() {
            if (!this.doesNotExpire) {
                this.$emit('update:expiryDate', '');
            }

            this.doesNotExpire = !this.doesNotExpire;
            this.$emit('update:doesNotExpire', this.doesNotExpire);
        },
    },
};
</script>

<style lang="scss" scoped>
    input[type="date"] {
        padding-top: 0.9rem;
        padding-bottom: 0.3rem;
        font-family: "Nunito";
    }

    ::v-deep {
        .checkbox {
            padding: 0.5rem 0;
            font-size: 1.2rem;
        }
    }

    .expiry-date {
        order: 2;
        @include min-width(lg) {
            order: 1;
        }
    }

    .issuing-country {
        order: 1;
        @include min-width(lg) {
            order: 2;
        }
    }

    .does-not-expire {
        order: 3;
        @include min-width(lg) {
            order: 3;
        }
    }

    .codice-fiscale {
        order: 4;
        @include min-width(lg) {
            order: 4;
        }
    }
</style>